// Metro tariff ingestion — turns a per-suburb trucking rate sheet (Custom Logistic
// "Monada <Port> <MODE> Sheet") into Monada rates bound to a postcode/named-location door.
//
// Sheet shape (one row per suburb):
//   LCL: POSTCODE/ZIPCODE | NAMED LOCATION | PORT | COUNTRY | MODE | MINIMUM FEE | PER M3 | PER TONNE | CURRENCY
//   FCL: POSTCODE/ZIPCODE | NAMED LOCATION | PORT | COUNTRY | MODE | 20' | 40' | CURRENCY
//   AIR: POSTCODE/ZIPCODE | NAMED LOCATION | PORT | COUNTRY | MODE | MINIMUM FEE | PER KG | CURRENCY
//
// Rates match on an exact `type:id` key (see src/server/db/rate.js), so the door location
// is bound to a DETERMINISTIC id — `${countryCode}:${postcode}:${slug(namedLocation)}` —
// that the search-time picker regenerates identically. The suburb is part of the key because
// the same postcode can carry different rates by suburb.

import { v4 as uuidv4 } from 'uuid';
import { formulaFromLCLWM } from '../../utils/Formula';

const DEFAULT_LCL_DENSITY = 1000; // 1 m³ : 1000 kg (W/M 1:1) — metro LCL & ocean LCL
const COUNTRY_MAP = { australia: 'AU', 'new zealand': 'NZ', 'new-zealand': 'NZ' };

function normCountry(c) {
    const s = String(c || '').trim();
    if (!s) return '';
    if (s.length === 2) return s.toUpperCase();
    return COUNTRY_MAP[s.toLowerCase()] || s.toUpperCase();
}

function num(v) {
    const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

export function slugifyLocation(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '');
}

// Australian and New Zealand postcodes are four digits, but Excel stores 0612 as the number 612.
// Read as-is that gave "Te Atatu (651)" and an id other 4-digit uploads of the same suburb don't
// match (Custom Logistic: 660 air rates, fixed in the data 2026-10-14).
export function normalizePostcode(postcode, countryCode) {
    const pc = String(postcode ?? '').trim();
    return ['AU', 'NZ'].includes(normCountry(countryCode)) && /^\d{1,3}$/.test(pc) ? pc.padStart(4, '0') : pc;
}

// Deterministic door-location id — reproduced identically at search time.
export function metroLocationId({ countryCode, postcode, namedLocation }) {
    return `${normCountry(countryCode)}:${String(postcode || '').trim()}:${slugifyLocation(namedLocation)}`;
}

export function metroDestination({ countryCode, postcode, namedLocation }) {
    const cc = normCountry(countryCode);
    return {
        type: 'postcode',
        id: metroLocationId({ countryCode: cc, postcode, namedLocation }),
        text: `${namedLocation} (${postcode})`,
        countryCode: cc,
        city: namedLocation,
    };
}

function perUnitField(title, formula, currency, productId, id = uuidv4()) {
    return { id, title, type: 'per-unit-type', values: { [productId]: { formula, currency } } };
}

function flatField(title, value, currency, id = uuidv4()) {
    return { id, title, type: 'flat', values: { flat: { value, currency } } };
}

// Flat ("per shipment") percentage surcharge — a flat field whose value carries the % formula.
// A fuel surcharge is a single per-shipment charge (a % of the whole transport cost), so it must
// be a flat field: a per-unit-type surcharge is charged once per product line, which over-counts
// the % the moment the rate is applied to an opportunity with more than one product.
function flatSurchargeOnField(title, pct, baseFieldId, currency, id = uuidv4()) {
    const ref = `field_${baseFieldId.replace(/-/g, '_')}`;
    return { id, title, type: 'flat', values: { flat: { formula: `max(0, ${pct}/100*((${ref})))`, currency } } };
}

function lclProduct(productId, densityRatio = DEFAULT_LCL_DENSITY) {
    return {
        id: productId, type: 'LCL', dangerous: false, quantity: 1, kgOrLbs: 'kg', cmOrInches: 'cm',
        calculations: [{ id: 'default', method: 'volume', volume: 1, weight: 1000, quantity: 1 }],
        densityRatio,
    };
}

function airProduct(productId) {
    return {
        id: productId, type: 'Air freight', dangerous: false, quantity: 1, kgOrLbs: 'kg', cmOrInches: 'cm',
        calculations: [{ id: 'default', method: 'chargeable-weight', chargeableWeight: 1, weight: 1, quantity: 1 }],
    };
}

function containerProduct(productId, containerType) {
    return { id: productId, type: containerType, dangerous: false, quantity: 1 };
}

// The FCL sheet only gives a container size (20'/40'), not a container class, but Monada
// product types are class-qualified ("20' Dry", "40' Dry", …) and the schema rejects a bare
// "20'"/"40'". Default to the standard Dry container (the overwhelming majority of FCL).
function fclContainerType(size) {
    return `${size} Dry`; // "20'" -> "20' Dry", "40'" -> "40' Dry"
}

function section(title, fields) {
    return { id: uuidv4(), title, offers: [{ id: uuidv4(), fields }] };
}

function makeRate({ transportationMethod, source, destination, product, sections, supplier, validFrom, validUntil, direction, transitTime, description }) {
    const now = Date.now();
    return {
        id: uuidv4(), type: 'contract', created: now, modified: now,
        transportationMethod, source, destination,
        supplier: supplier || { organization: 'Metro Tariff' },
        attributes: { source: 'metro-tariff', metroDirection: direction },
        product,
        offer: {
            id: uuidv4(), created: now, validFrom: validFrom || '', validUntil: validUntil || '',
            transshipment: '', routes: [{ source, destination }], sections,
            // The sheet's transit days and route ("LINEHAUL TRUCK", "COASTAL SHIP + LINEHAUL TRUCK"):
            // two services on the same suburb (truck vs coastal) are otherwise indistinguishable.
            ...(Number.isFinite(transitTime) ? { transitTime } : {}),
            ...(description ? { description } : {}),
        },
    };
}

// Deep clone so the import/export copies of a row don't share field object references.
const clone = (x) => JSON.parse(JSON.stringify(x));

/**
 * Convert parsed metro-tariff rows into Monada rates.
 * @param {object[]} rows   parsed sheet rows (header-keyed objects, as from XLSX sheet_to_json)
 * @param {object}   port   resolved source port location: { type:'port', id, text, countryCode }
 * @param {object}   opts   { fuelSurchargePct=33, fclBookingFee=690, fclBookingFeeCurrency='AUD',
 *                            validFrom, validUntil, supplier, directions=['import','export'] }
 * @returns {{ rates: object[], errors: {row:number, reason:string}[], counts: object }}
 */
export function convertMetroTariff({ rows, port, ...opts }) {
    const {
        fuelSurchargePct = 33, fclBookingFee = 690, fclBookingFeeCurrency = 'AUD',
        validFrom = '', validUntil = '', supplier, directions = ['import', 'export'],
    } = opts || {};
    if (!port || port.type !== 'port') throw new Error('convertMetroTariff: a resolved source `port` location is required');

    const rates = [];
    const errors = [];
    const counts = { LCL: 0, FCL: 0, AIR: 0, skipped: 0 };

    (rows || []).forEach((row, i) => {
        const rawPostcode = String(row['POSTCODE/ZIPCODE'] ?? row.postcode ?? '').trim();
        const namedLocation = String(row['NAMED LOCATION'] ?? row.namedLocation ?? '').trim();
        const mode = String(row['MODE'] ?? row.mode ?? '').trim().toUpperCase();
        const currency = (String(row['CURRENCY'] ?? row.currency ?? 'AUD').trim() || 'AUD').toUpperCase();
        const countryCode = normCountry(row['COUNTRY'] ?? row.country ?? port.countryCode);
        const transitTime = parseInt(row['TRANSIT TIME'], 10);
        const description = String(row['ROUTE INFORMATION'] ?? '').trim();

        const postcode = normalizePostcode(rawPostcode, countryCode);
        if (!postcode || !namedLocation) { errors.push({ row: i, reason: 'missing postcode or named location' }); counts.skipped++; return; }
        const door = metroDestination({ countryCode, postcode, namedLocation });

        // Each variant is a distinct rate template: { transportationMethod, product, sections }
        const variants = [];
        if (mode === 'LCL') {
            const productId = uuidv4();
            const min = num(row['MINIMUM FEE']);
            const perCbm = num(row['PER M3']);
            const perTon = num(row['PER TONNE']);
            const basId = uuidv4();
            const bas = perUnitField('TRANSPORT', formulaFromLCLWM({ perTon, perCbm, densityRatio: DEFAULT_LCL_DENSITY, minPerTon: min, minPerCbm: min }), currency, productId, basId);
            const sections = [
                section('Transport', [bas]),
                // Fuel surcharge is a per-shipment % of transport, so it must be a flat field —
                // a per-unit-type surcharge is charged once per product line and over-counts.
                section('Surcharges', [flatSurchargeOnField('FUEL SURCHARGE', fuelSurchargePct, basId, currency)]),
            ];
            variants.push({ transportationMethod: 'land', product: lclProduct(productId), sections });
            counts.LCL++;
        } else if (mode === 'FCL') {
            for (const size of ["20'", "40'"]) {
                const val = num(row[size]);
                if (!val) continue;
                const productId = uuidv4();
                const basId = uuidv4();
                const sections = [
                    section('Transport', [flatField('TRANSPORT', val, currency, basId)]),
                    section('Surcharges', [
                        // FCL transport is a per-shipment flat charge, so its fuel surcharge must be
                        // per-shipment (flat) too for the % formula to evaluate against it.
                        flatSurchargeOnField('FUEL SURCHARGE', fuelSurchargePct, basId, currency),
                        flatField('PORT BOOKING FEE', fclBookingFee, fclBookingFeeCurrency),
                    ]),
                ];
                variants.push({ transportationMethod: 'land', product: containerProduct(productId, fclContainerType(size)), sections });
            }
            counts.FCL++;
        } else if (mode === 'AIR') {
            const productId = uuidv4();
            const min = num(row['MINIMUM FEE']);
            const perKg = num(row['PER KG']);
            const basId = uuidv4();
            // `x` is the charge line's own multiplier, which for an air product is its chargeable
            // weight. weightKg reads as if it would work, but it is only defined for a line
            // carrying an LCL product — on air it throws Undefined symbol, and the caller swallows
            // that and prices the charge at zero, so the transport leg silently came to nothing.
            //
            // Written exactly as formulaFromMinAndTiers writes it — `max(min,x * rate)`, multiplier
            // first and no space after the comma — because formulaToParams reads that shape and
            // nothing else. Spelt any other way it still prices correctly but cannot be read back,
            // so the rate search shows the word "formula" where the rate should be. Always wrapped
            // in max, using a floor of 0 when the sheet sets no minimum, since a bare product is
            // not a shape the parser recognises either.
            const formula = `max(${min > 0 ? min : 0},x * ${perKg})`;
            const sections = [
                section('Transport', [perUnitField('TRANSPORT', formula, currency, productId, basId)]),
                // Flat for the same reason as LCL: the fuel surcharge is one per-shipment % of the
                // whole transport cost, and a per-unit-type one over-counts on a multi-product quote.
                section('Surcharges', [flatSurchargeOnField('FUEL SURCHARGE', fuelSurchargePct, basId, currency)]),
            ];
            // This sheet prices the airport↔door trucking leg, not the flight: a land rate carrying
            // an air product, the same way the LCL and FCL rows are land rates carrying sea products.
            variants.push({ transportationMethod: 'land', product: airProduct(productId), sections });
            counts.AIR++;
        } else {
            errors.push({ row: i, reason: `unknown MODE "${mode}"` });
            counts.skipped++;
            return;
        }

        for (const v of variants) {
            for (const direction of directions) {
                const source = direction === 'import' ? port : door;
                const destination = direction === 'import' ? door : port;
                rates.push(makeRate({
                    transportationMethod: v.transportationMethod,
                    source, destination,
                    product: clone(v.product),
                    sections: clone(v.sections),
                    supplier, validFrom, validUntil, direction, transitTime, description,
                }));
            }
        }
    });

    return { rates, errors, counts };
}
